import { InspectionsTranslationsDictionary } from "../helpers/translations";

export const CANADA_PROVINCES: TCanadaProvinces[] = [
    "ON",
    "QC",
    "NS",
    "NB",
    "MB",
    "BC",
    "PE",
    "SK",
    "AB",
    "NL",
    "YT",
    "NT",
    "NU",
    "ONTARIO",
    "QUEBEC",
    "QUÉBEC",
    "NOVA SCOTIA",
    "NEW BRUNSWICK",
    "MANITOBA",
    "PRINCE EDWARD ISLAND",
    "SASKATCHEWAN",
    "BRITISH COLUMBIA",
    "ALBERTA",
    "NEWFOUNDLAND",
    "NEWFOUNDLAND AND LABRADOR",
    "YUKON",
    "NORTHWEST TERRITORIES",
    "NUNAVUT",
    "N.L.",
    "NFLD",
    "PEI",
    "P.E.I.",
    "N.S.",
    "QUE",
    "ONT",
    "MAN",
    "SASK",
    "ALTA",
    "B.C.",
    "Y.T.",
    "N.W.T.",
    "NVT"
];

const ONTARIO_ABBREVIATIONS = ["ON", "ONTARIO", "ONT"];

export enum Province {
    Alberta = "ALTA",
    BritishColumbia = "BC",
    Manitoba = "MB",
    NewBrunswick = "NB",
    NovaScotia = "NS",
    NewfLab = "NFLD",
    Ontario = "ON",
    PrinceEdwardIsland = "PEI",
    Saskatchewan = "SK",
    Quebec = "QC"
}

export enum Country {
    Australia = "AUSTRALIA"
}

export function isFromProvince(provinces: Province[], region: string) {
    const normalizedRegion = region.trim();
    return provinces.some((province: Province) => {
        switch (province) {
            case Province.Alberta:
                return normalizedRegion.match(/^(alberta|ab)$/gi);
            case Province.BritishColumbia:
                return normalizedRegion.match(
                    /^(britishcolumbia|bc|british[ .]columbia)$/gi
                );
            case Province.Manitoba:
                return normalizedRegion.match(/^(manitoba|mb)$/gi);
            case Province.NewBrunswick:
                return normalizedRegion.match(
                    /^(newbrunswick|nb|new[ .]brunswick)$/gi
                );
            case Province.NewfLab:
                return normalizedRegion.match(
                    /^(nl|nfld|newfoundland[ .]and[ .]labrador|newfoundlandandlabrador)$/gi
                );
            case Province.NovaScotia:
                return normalizedRegion.match(
                    /^(novascotia|ns|nova[ .]scotia)$/gi
                );
            case Province.Ontario:
                return normalizedRegion.match(/^(on|ontario)$/gi);
            case Province.PrinceEdwardIsland:
                return normalizedRegion.match(
                    /^(pe|pei|princeedwardisland|prince[ .]edward[ .]island)$/gi
                );
            case Province.Saskatchewan:
                return normalizedRegion.match(/^(sk|saskatchewan)$/gi);
            case Province.Quebec:
                return normalizedRegion.match(/^(qc|qu[eé]bec)$/gi);
            default:
                return false;
        }
    });
}

export function isFromAustralia(region: string) {
    return region.toUpperCase() === Country.Australia;
}

export function isFromCanada(licenseState: string) {
    return (
        CANADA_PROVINCES.indexOf(
            licenseState.toUpperCase().trim() as TCanadaProvinces
        ) !== -1
    );
}

export function isDeviceFromOntario(licenseState: string) {
    return (
        ONTARIO_ABBREVIATIONS.indexOf(licenseState.toUpperCase().trim()) > -1
    );
}

const DVIR_DECLARATION_GENERIC = "This {0} was inspected in accordance with regional and/or federal regulations.";

const PROVINCE_CERTIFICATION_DECLARATIONS: [Province[], string][] = [
    [[Province.Alberta], InspectionsTranslationsDictionary.CertificationDeclarationAlberta],
    [[Province.BritishColumbia, Province.NovaScotia], InspectionsTranslationsDictionary.CertificationDeclarationBcNovaScotia],
    [[Province.NewfLab, Province.PrinceEdwardIsland, Province.Quebec, Province.Saskatchewan, Province.NewBrunswick], InspectionsTranslationsDictionary.CertificationDeclarationGeneralCanada],
    [[Province.Manitoba], InspectionsTranslationsDictionary.CertificationDeclarationManitoba],
    [[Province.Ontario], InspectionsTranslationsDictionary.CertificationDeclarationOntario]
];

/**
 * Returns the DVIR declaration string for the given region, using {0} as the asset placeholder.
 * Matches the format consumed by dvir/helper.ts via the old Backbone Translator.
 */
export function getDvirDeclarationString(region: string): string {
    const match = PROVINCE_CERTIFICATION_DECLARATIONS.find(([provinces]) => isFromProvince(provinces, region));
    return match ? match[1] : DVIR_DECLARATION_GENERIC;
}

/**
 * Returns the certification declaration translation string for the given licenseState.
 *
 * Answers only "which jurisdiction's wording", never "should a declaration appear at all" —
 * that is utils/regulatoryInspection.isRegulatoryInspection.
 * Fallback chain: licenseState → each comma-separated segment of formattedAddress → generic.
 * The caller is responsible for translating and substituting {0} with the asset name.
 */
export function getCertificationDeclarationText(licenseState: string | undefined, formattedAddress?: string): string {
    const region = licenseState ?? "";
    const licenseMatch = PROVINCE_CERTIFICATION_DECLARATIONS.find(([provinces]) => isFromProvince(provinces, region));
    if (licenseMatch) {
        return licenseMatch[1];
    }

    if (formattedAddress) {
        for (const segment of formattedAddress.split(",")) {
            const gpsMatch = PROVINCE_CERTIFICATION_DECLARATIONS.find(([provinces]) => isFromProvince(provinces, segment.trim()));
            if (gpsMatch) {
                return gpsMatch[1];
            }
        }
    }

    return InspectionsTranslationsDictionary.CertificationDeclarationGeneric;
}

export function isValidProvince(region: string) {
    if (
        isFromProvince(
            [
                Province.Alberta,
                Province.BritishColumbia,
                Province.Manitoba,
                Province.NewBrunswick,
                Province.NewfLab,
                Province.NovaScotia,
                Province.Ontario,
                Province.PrinceEdwardIsland,
                Province.Quebec,
                Province.Saskatchewan
            ],
            region
        ) ||
        isFromAustralia(region)
    ) {
        return true;
    }

    return false;
}
